import { useState, useEffect, useCallback, MouseEventHandler } from "react";
import moment, { Moment } from "moment";
import { SelectProps, SelectChangeEvent } from "@mui/material";
import { MomentChangeHandler } from "@core/interfaces/moment-change-handler";
import { useStore } from "@ui/hooks/useStore";

import getLiElementId from "@utils/getLiElementId";

export interface IState {
    firstLoading: boolean;
    addDialogOpen: boolean;
    fromDateFilterCurrent: Moment;
    toDateFilterCurrent: Moment;
    speedDialHover: boolean;
    addGroupVisible: boolean;
}

const useHistoryMenu = () => {
    const { map, lang, user, groups, app, devices, telemetry } = useStore();
    const fromDateFilterCurrent = moment(map.fromDateFilter);
    const toDateFilterCurrent = moment(map.toDateFilter);

    const initState: IState = {
        firstLoading: true,
        addDialogOpen: false,
        fromDateFilterCurrent,
        toDateFilterCurrent,
        speedDialHover: false,
        addGroupVisible: false,
    };
    const [state, setState] = useState(initState);

    const handleFromDateChange: MomentChangeHandler = useCallback(
        (date) => {
            setState((prev) => ({ ...prev, fromDateFilterCurrent: date }));

            if (moment(date).isValid()) {
            if (date.isValid() && moment(date).isValid()) {
                const tz = user.settings.timezone;
                map.setFromDateFilter(
                    date.utc(true).subtract(tz, "m").toDate()
                );                
            } else {
                console.log("Invalid date provided");
            }
        }
        },
        [user.settings.timezone]
    );
    const handleToDateChange: MomentChangeHandler = useCallback(
        (date) => {
            setState((prev) => ({ ...prev, toDateFilterCurrent: date }));
            if (moment(date).isValid()) {
            if (date.isValid()) {
                const tz = user.settings.timezone;
                map.setToDateFilter(date.utc(true).subtract(tz, "m").toDate());
            }
        }
        },
        [user.settings.timezone]
    );
    const handleDateFilterChange: SelectProps<string>["onChange"] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const dateFilter = e.target.value;
            map.setDateFilter(dateFilter);
        },
        []
    );
    
    const handleDeviceSelect: SelectProps<string>["onChange"] = useCallback(
        (e: SelectChangeEvent<string>) => {
            const imei = e.target.value;
            if (imei && map.selectedDevice !== imei) {
                map.setSelectedDevice(imei);
            }
        },
        []
    );
    const handleAddGroupOpen = useCallback(() => {
        setState((prev) => ({ ...prev, addGroupVisible: true }));
    }, []);
    const handleAddGroupClose = useCallback(() => {
        setState((prev) => ({ ...prev, addGroupVisible: false }));
    }, []);
    const handleAddDeviceInDbOpen = useCallback(() => {
        app.openAddDeviceInDb();
    }, []);
    const handleAddDeviceInDbClose = useCallback(() => {
        app.closeAddDeviceInDb();
    }, []);
    const handleAddDeviceOpen = useCallback(() => {
        app.openAddDevice();
    }, []);
    const handleAddDeviceClose = useCallback(() => {
        app.closeAddDevice();
    }, []);
    const handleAddDialog = useCallback(() => {
        setState((prev) => ({ ...prev, addDialogOpen: !prev.addDialogOpen }));
    }, []);
    const handleAddDiologClose = useCallback(() => {
        setState((prev) => ({ ...prev, addDialogOpen: false }));
    }, []);
    const handleSpeedDialHoverStart = useCallback(() => {
        setState((prev) => ({ ...prev, speedDialHover: true }));
    }, []);
    const handleSpeedDialHoverStop = useCallback(() => {
        setState((prev) => ({ ...prev, speedDialHover: false }));
    }, []);
    const handleSwitchTrackMode = useCallback(() => {
        map.switchTrackMode();
    }, []);
    const handleUpdateClick = useCallback(() => {
        map.updateHistoryCoordsList();
        telemetry.setTelemetryVisible();
        if (!map.selectedDevice) return;
        const [device] = map.lastCoordsList.filter((device) => {
            return device.imei === map.selectedDevice;
        });

        if (device) {
            const { lat: latCoord, lon: lonCoord } = device.lbsCoord;
            const { lat, lon } = device.gpsCoord;

            const gpsDate = device.gpsDate || 0;
            const lbsDate = device.lbsDate || 0;

            if (lat && lon && gpsDate > lbsDate) {
                map.setCurrentPosition([lat, lon]);
                map.setPosition(device.gpsCoord);
            } else if (latCoord && lonCoord) {
                map.setCurrentPosition([latCoord, lonCoord]);
                map.setPosition(device.lbsCoord);
            }
            map.setZoom(18);
        }
    }, []);

    const handleDeviceInfoOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const imei = getLiElementId(e.target as HTMLElement);
            if (imei) {
                devices.setInfoDeviceImei(imei);
                devices.setInfoDeviceSettings(imei);
                app.openDeviceInfo();
            }
        },
        []
    );

    const handleDeviceSettingsOpen: MouseEventHandler<HTMLElement> =
        useCallback(
            (e) => {
                e.preventDefault();
                e.stopPropagation();
                const imei = getLiElementId(e.target as HTMLElement);
                const callback = () => {
                    imei && devices.setSettingDeviceImei(imei);
                    app.openDeviceSettings();
                    setState((prev) => ({
                        ...prev,
                        deviceSettingsVisible: true,
                    }));
                };
                if (!imei) return;
                const [settings] = devices.settingsList.filter(
                    (val) => val.imei === imei
                );
                const isSettings = true;
                //settings && settings.status && !settings.status.error;
                if (!isSettings) return;
                if (!app.deviceSettingsVisible) {
                    callback();
                } else {
                    setState((prev) => ({
                        ...prev,
                        isSettingsCloseCall: true,
                    }));
                    setState((prev) => ({
                        ...prev,
                        devSettingsCallback: callback,
                    }));
                    app.setDeviceSettingsClosing();
                }
            },
            [app.deviceSettingsVisible]
        );

    const handleDeviceDescriptionOpen: MouseEventHandler<HTMLElement> =
        useCallback((e) => {
            e.preventDefault();
            e.stopPropagation();
            const imei = getLiElementId(e.target as HTMLElement);
            if (imei) {
                devices.setEditedDeviceImei(imei);
                app.openDeviceEdit();
            }
        }, []);

    const handleDeviceBtnHoverStart = useCallback(() => {
        setState((prev) => ({ ...prev, deviceBtnHover: true }));
    }, []);
    const handleDeviceBtnHoverStop = useCallback(() => {
        setState((prev) => ({ ...prev, deviceBtnHover: false }));
    }, []);

    const handlers = {
        handleFromDateChange,
        handleToDateChange,
        handleDateFilterChange,
        handleDeviceSelect,
        handleAddGroupOpen,
        handleAddGroupClose,
        handleAddDeviceInDbOpen,
        handleAddDeviceInDbClose,
        handleAddDeviceOpen,
        handleAddDeviceClose,
        handleAddDialog,
        handleAddDiologClose,
        handleSpeedDialHoverStart,
        handleSpeedDialHoverStop,
        handleSwitchTrackMode,
        handleUpdateClick,

        handleDeviceInfoOpen,
        handleDeviceSettingsOpen,
        handleDeviceDescriptionOpen,
        handleDeviceBtnHoverStop,
        handleDeviceBtnHoverStart,
    };

    useEffect(() => {
        setState((prev) => ({ ...prev, fromDateFilterCurrent }));
    }, [fromDateFilterCurrent.milliseconds()]);
    useEffect(() => {
        setState((prev) => ({ ...prev, toDateFilterCurrent }));
    }, [toDateFilterCurrent.milliseconds()]);
    useEffect(() => {
        const groupsLen = groups.groupsList.length;
        const devicesLen = devices.deviceList.length;
        if (
            (app.page === "history" || app.page === "support") &&
            state.firstLoading &&
            groupsLen > 0 &&
            devicesLen > 0 &&
            !map.selectedGroupId
        ) {
            let id = localStorage.getItem("groupId");
            if (!id) {
                [{ id }] = groups.groupsList.filter(
                    (group) => group.is_default
                );
            }
            const listLen = devices.deviceList
                .filter((device) => device.groupId === id)
                .map(({ imei }) => imei).length;
            if (listLen > 0) {
                map.setSelectedGroupId(id);
                setState((prev) => ({ ...prev, firstLoading: false }));
            }
        }
    }, [groups.groupsList, devices.deviceList, app.page]);
                    
    return {
        map,
        lang,
        devices,
        groups,
        user,
        state,
        handlers,
    };
};

export default useHistoryMenu;
